import type { Editor } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { EditorFeatures } from '../../types';

interface HeadingToolbarProps {
  editor: Editor | null;
  features: Pick<EditorFeatures, 'textStyles'>;
}

export const HeadingToolbar: React.FC<HeadingToolbarProps> = ({ editor, features }) => {
  const [showHeadingDropdown, setShowHeadingDropdown] = useState(false);
  const headingDropdownRef = useRef<HTMLDivElement | null>(null);

  const getCurrentHeadingLabel = useCallback(() => {
    if (!editor) {
      return '標準';
    }
    if (editor.isActive('heading', { level: 1 })) {
      return '見出し1';
    }
    if (editor.isActive('heading', { level: 2 })) {
      return '見出し2';
    }
    if (editor.isActive('heading', { level: 3 })) {
      return '見出し3';
    }
    return '標準';
  }, [editor]);

  const handleDropdownItemClick = (action: () => void) => {
    if (!editor) {
      return;
    }

    editor.view.dom.focus({ preventScroll: true });
    action();
    setShowHeadingDropdown(false);
  };

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        headingDropdownRef.current &&
        !headingDropdownRef.current.contains(event.target as Node)
      ) {
        setShowHeadingDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!features.textStyles || !editor) {
    return null;
  }

  return (
    <div
      ref={headingDropdownRef}
      className='dropdownWrapper'
      title='フォントサイズ'
    >
      <button
        className='headingDropdownButton toolbar-button'
        onClick={() => setShowHeadingDropdown(!showHeadingDropdown)}
        type='button'
      >
        {getCurrentHeadingLabel()}
      </button>
      {showHeadingDropdown && (
        <div className='dropdownMenu fontSizeMenu'>
          <div
            className='dropdownItem'
            onClick={() => {
              handleDropdownItemClick(() => editor.chain().setParagraph().run());
            }}
          >
            標準
          </div>
          <div
            className='dropdownItem'
            onClick={() => {
              handleDropdownItemClick(() => editor.chain().toggleHeading({ level: 1 }).run());
            }}
          >
            見出し1
          </div>
          <div
            className='dropdownItem'
            onClick={() => {
              handleDropdownItemClick(() => editor.chain().toggleHeading({ level: 2 }).run());
            }}
          >
            見出し2
          </div>
          <div
            className='dropdownItem'
            onClick={() => {
              handleDropdownItemClick(() => editor.chain().toggleHeading({ level: 3 }).run());
            }}
          >
            見出し3
          </div>
        </div>
      )}
    </div>
  );
};
